/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { getDownloadURL } from "firebase/storage";
import Page from "components/common_components/Page/Page";
import { useDispatch } from "react-redux";
import { notifyError, notifyInfo, notifySuccess } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import ModulesContext from "../ModulesContext";
import Grid from "@material-ui/core/Grid";
import { ConfirmDialog } from "components/common_components/Dialogs";
import { sanitizeFileName } from "utils/files";
import { hasElements } from "utils/arrays";
import Icon from "components/common_components/Icon";
import Button from "components/common_components/Button";
import useFilesUploader from "components/common_components/hooks/useFilesUploader/useFilesUploader";
import FileExplorer from "./FileExplorer";

function Attachments({ onCancel }) {
    const dispatch = useDispatch();
    // eslint-disable-next-line no-unused-vars
    const [filesToUpload, fileNames, _, reset, uploader] = useFilesUploader({
        requestOrigin: false,
    });
    const [showDragzone, setShowDragzone] = useState(false);
    const [selectedFile, setSelectedFile] = useState(undefined);
    const [isUploading, setIsUploading] = useState(false);
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });

    const { Attachments, currentFileCase, isLoading, attachments, getAttachmentsAsync, Storage, module } = useContext(ModulesContext);

    const STORAGE_DIR = `modules/${module.dbName}/attachments/${currentFileCase.id}`;
    const [currentDirectory, setCurrentDirectory] = useState(STORAGE_DIR);
    const [moveFileMode, setMoveFileMode] = useState(false);
    const [targetDirectory, setTargetDirectory] = useState(undefined);
    const [selectedFilesToMove, setSelectedFilesToMove] = useState([]);
    const [movingFilesLoading, setMovingFilesLoading] = useState(false);

    const onTargetDirChange = (newDirectory) => {
        setTargetDirectory(newDirectory);
    };

    function initAttachFile() {
        reset();
        setSelectedFile(undefined);
        setShowDragzone(true);
    }

    function initMoveFiles() {
        setMoveFileMode(true);
        setSelectedFile(undefined);
        setShowDragzone(false);
    }

    const cancelMoveFiles = () => {
        setMoveFileMode(false);
        setTargetDirectory(undefined);
        setSelectedFilesToMove([]);
    };

    function cancelAttachFile() {
        reset();
        setSelectedFile(undefined);
        setShowDragzone(false);
    }

    useEffect(() => {
        if (!currentFileCase) {
            dispatch(notifyError("You have to select a client file first!"));
            onCancel();
        }
    }, [currentFileCase]);

    async function onDeleteSuccess(file) {
        dispatch(notifyInfo("Removed successfully!"));
        setSelectedFile(undefined);
        await Storage.deleteAsync({
            path: `${currentDirectory}/${file.name}`,
        });
        await getAttachmentsAsync();
    }

    function onDeleteError(error) {
        dispatch(notifyError(error.message));
    }

    function onDeleteHandler(file) {
        setConfirmDialogProps((prev) => ({
            ...prev,
            open: true,
            title: "Delete attachment",
            content: "Are you sure you want to delete this attachment?",
            primaryButtonText: "Yes, delete",
            onPrimaryClick: () => {
                Attachments.deleteAll({
                    ids: [file.id],
                    onSuccess: () => {
                        onDeleteSuccess(file);
                        prev.onClose();
                    },
                    onError: (error) => {
                        onDeleteError(error);
                        prev.onClose();
                    },
                });
            },
        }));
    }

    function getRelativeTargetDirectory() {
        if (!targetDirectory) return "";
        if (targetDirectory === STORAGE_DIR) return currentFileCase.fileNo;
        return targetDirectory.replace(STORAGE_DIR, "").replace(/^\//, "");
    }

    function onMoveFilesHandler() {
        // Validate if the target directory is set
        if (!targetDirectory) {
            dispatch(notifyError("Please select a target directory to move the files."));
            return;
        }
        // Validate if there are files selected to move
        if (!hasElements(selectedFilesToMove)) {
            dispatch(notifyError("Please select files to move."));
            return;
        }
        // Validate if the selected file is in the target directory
        if (targetDirectory === currentDirectory) {
            dispatch(notifyError("You cannot move files to the same directory."));
            return;
        }
        // Show confirmation dialog
        setConfirmDialogProps((prev) => ({
            ...prev,
            open: true,
            title: "Move attachments",
            content: `Are you sure you want to move ${
                selectedFilesToMove.length
            } attachments to the folder ${getRelativeTargetDirectory()}?`,
            primaryButtonText: "Yes, move",
            onPrimaryClick: async () => {
                setMovingFilesLoading(true);
                prev.onClose();
                try {
                    for (const file of selectedFilesToMove) {
                        const { newUrl } = await Storage.moveFile(file.url, `${targetDirectory}/${file.name}`);
                        const response = await Attachments.changeUrlAsync(file.id, newUrl);
                        if (response?.status !== 204) {
                            const err = new Error(`Error moving the file ${file.name}`);
                            err.data = file;
                            throw err;
                        }
                    }
                    dispatch(notifySuccess(`Files moved successfully!`));
                    await getAttachmentsAsync();
                } catch (error) {
                    console.error(error);
                    dispatch(notifyError(error.message || "Unexpected error."));
                } finally {
                    setMovingFilesLoading(false);
                    setSelectedFilesToMove([]);
                }
            },
        }));
    }

    async function onSubmitHandler() {
        const requests = [];

        filesToUpload.forEach((file) => {
            const request = new Promise(async (resolve, reject) => {
                const customName = fileNames[file.name] || file.name;
                try {
                    const fileExtension = `.${file.name.split(".").pop()}`;
                    const fileName = sanitizeFileName(customName.replace(fileExtension, ""));
                    const snap = await Storage.uploadAsync({
                        path: `${currentDirectory}/${fileName}${fileExtension}`,
                        file,
                    });
                    const url = await getDownloadURL(snap.ref);
                    const response = await Attachments.addAsync({
                        name: `${fileName}${fileExtension}`,
                        url,
                        date: new Date().getTime(),
                        fileCaseId: currentFileCase.id,
                    });
                    if (response && response.status === 200) {
                        resolve(response);
                    } else {
                        const err = new Error(`Error uploading the file ${customName}`);
                        err.data = file;
                        reject(err);
                    }
                } catch (error) {
                    const err = new Error(`Error uploading the file ${customName}`);
                    err.httpError = error;
                    err.data = file;
                    reject(err);
                }
            });

            requests.push(request);
        });

        if (requests.length > 0) {
            setIsUploading(true);
            Promise.all(requests)
                .then(async () => {
                    dispatch(notifySuccess(`Uploaded successfully!`));
                    cancelAttachFile();
                    await getAttachmentsAsync();
                })
                .catch((error) => {
                    dispatch(notifyError(error.message));
                })
                .finally(() => {
                    setIsUploading(false);
                });
        }
    }

    useEffect(() => {
        if (currentDirectory) {
            setSelectedFile(undefined);
        }
    }, [currentDirectory]);

    return (
        <Page className="attachments">
            {isLoading ? (
                <LoadingPage />
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        {showDragzone && (
                            <Grid container justifyContent="flex-end" style={{ paddingTop: 20 }}>
                                <div className="button-group">
                                    <Button secondary small outline onClick={cancelAttachFile}>
                                        <Icon name="cancel" size="xs" color="secondary" />
                                        <span>Cancel</span>
                                    </Button>
                                    {hasElements(filesToUpload) && (
                                        <Button secondary small loading={isUploading} onClick={onSubmitHandler}>
                                            <Icon name="upload" size="sm" color="white" />
                                            <span>Upload</span>
                                        </Button>
                                    )}
                                </div>
                            </Grid>
                        )}
                    </Grid>
                    {showDragzone && (
                        <Grid item xs={12}>
                            {uploader()}
                        </Grid>
                    )}
                    <Grid item xs={12} style={{ paddingTop: 20 }}>
                        <Grid container direction="row" justifyContent="space-between">
                            <Grid item xs={!selectedFile ? 5 : 4}>
                                <FileExplorer
                                    rootPath={STORAGE_DIR}
                                    onDirectoryChange={setCurrentDirectory}
                                    rootTitle={currentFileCase.fileNo}
                                    onFileSelected={setSelectedFile}
                                    selectedFile={selectedFile}
                                    onDeleteFile={onDeleteHandler}
                                    attachmentRecords={attachments}
                                    allowCreateFolder={!moveFileMode}
                                    isMovingFilesMode={moveFileMode}
                                    initAttachFiles={initAttachFile}
                                    initMoveFiles={initMoveFiles}
                                    selectedFilesToMove={selectedFilesToMove}
                                    onFilesToMoveChange={setSelectedFilesToMove}
                                    showToolbar={!showDragzone && !moveFileMode}
                                />
                            </Grid>
                            {moveFileMode && (
                                <Grid item xs={2}>
                                    <Grid
                                        container
                                        direction="column"
                                        justifyContent="center"
                                        alignItems="center"
                                        style={{ height: "100%", gap: "1rem" }}
                                    >
                                        <Button
                                            secondary
                                            small
                                            disabled={!targetDirectory || !hasElements(selectedFilesToMove)}
                                            onClick={onMoveFilesHandler}
                                            loading={movingFilesLoading}
                                        >
                                            <span>Move</span>
                                            <Icon name="move-to-folder" size="lg" color="white" />
                                        </Button>

                                        {!movingFilesLoading && (
                                            <Button secondary small outline onClick={cancelMoveFiles}>
                                                <Icon name="cancel" size="xs" color="secondary" />
                                                <span>Cancel</span>
                                            </Button>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            {moveFileMode && !selectedFile && (
                                <Grid item xs={5}>
                                    <FileExplorer
                                        rootPath={STORAGE_DIR}
                                        onDirectoryChange={onTargetDirChange}
                                        rootTitle={currentFileCase.fileNo}
                                        attachmentRecords={attachments}
                                        allowAttachFiles={false}
                                        allowMoveFiles={false}
                                        disableFiles
                                    />
                                </Grid>
                            )}
                            {selectedFile && !moveFileMode && (
                                <Grid item xs={8}>
                                    <iframe
                                        src={selectedFile ? selectedFile.url : undefined}
                                        frameBorder="0"
                                        title="Viewer"
                                        width="100%"
                                        height="900px"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            )}
            <ConfirmDialog {...confirmDialogProps} />
        </Page>
    );
}

Attachments.propTypes = {
    onCancel: PropTypes.func,
};

Attachments.defaultProps = {
    onCancel: () => {},
};

export default withRouter(Attachments);
